import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import { Button, Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useState } from 'react';
import css from './SortButton.module.scss';

export interface SortOptionItem {
  value: string;
  label: string;
}

/**
 * A modifier that applies *alongside* the chosen sort rather than replacing it — the Libraries
 * page's "Pinned first".
 *
 * Exported and shared with `MobileSortDrawer` so the desktop menu and the mobile drawer are
 * structurally prevented from drifting, rather than each retyping the shape behind a comment
 * promising they won't.
 */
export interface SortToggle {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface SortButtonProps {
  /** Label shown on the button — the current selection (e.g. "Last modified"). */
  selectedLabel: string;
  /**
   * Optional muted suffix after the sort name, for a modifier that is also in effect (the
   * Libraries page passes "Pinned first"). Rendered and styled here rather than injected as a
   * node by the caller: the button root sets `color: white !important`, so a caller-supplied
   * span had to fight it with `!important` from a page stylesheet, which put part of this
   * component's appearance — and the separator's spacing — outside the component.
   */
  qualifier?: string;
  /** Current selected value (highlighted with a check in the menu). */
  value: string;
  /** Options grouped for display; a divider is rendered between groups. */
  options: SortOptionItem[][];
  onChange: (value: string) => void;
  /** Button tooltip, e.g. "Sort libraries by". */
  tooltip?: string;
  /** Menu header. Defaults to "Sort by". */
  headerLabel?: string;
  /**
   * Optional checkable row after the option groups. Omitted by every other caller, which is why
   * it's optional: this component is shared with the Library media page, and that surface must
   * stay exactly as it is.
   */
  toggle?: SortToggle;
  /**
   * Abbreviate to the bare sort glyph — drops `selectedLabel`, `qualifier` and the chevron — for a
   * caller that seats it inside a `filter-button-group` pill beside the Filters glyph.
   *
   * Costlier than abbreviating that Filters pill, and worth being explicit about why it's still
   * acceptable: this button's face is the only place the CURRENT sort is stated, so compacting it
   * hides live state rather than just a name. The tooltip picks that up (`tooltip` gains the
   * selection, qualifier included), and the menu shows it checked — but a tooltip is hover-only, so
   * this is a real downgrade, taken because the labelled face is 268px at its widest and that is
   * what made two header rows overflow.
   *
   * Not solved with a count badge like the Filters pill: "sorted" is not a yes/no the way "filtered"
   * is — there is always a sort, so a badge would be permanently lit and say nothing.
   *
   * Both header callers set this from their row's width (`RIGHT_LABELS_BELOW` in `LibraryContent` and
   * in `LibrariesNav`), so the face survives wherever the row can afford it.
   */
  compact?: boolean;
  /**
   * `text` for a control inside a panel rather than a page header — the media modal's Comments
   * tab. Plain words, "Sort by Timecode ▾", instead of the frosted pill: in a panel set at 12px
   * the pill was the heaviest thing on screen, and on its own row it read as a section of its
   * own. The words also say what the value is; "Commenter" alone on a pill reads like a filter.
   * Only the face changes; the menu is the same.
   *
   * `ghost` for a line of quiet view options (the library's search line): the pill's glyph, words
   * and chevron, with no fill until hovered, so it doesn't outweigh the actions on the same line.
   */
  variant?: 'pill' | 'text' | 'ghost';
  /**
   * Called instead of opening the menu, for a caller that shows its own picker there — on a
   * phone, `MobileSortDrawer`. Lets one face serve both layouts, so the desktop and mobile
   * versions of a sort control cannot look different. Omit it and the button opens its menu.
   */
  onRequestOpen?: () => void;
}

/**
 * Sort control patterned after the {@link OmniFilter} "Filters" button: a frosted
 * pill with a sort icon, the current selection's label, and a chevron, opening a
 * menu of grouped sort options. Purely presentational — the caller owns the option
 * set, the current value, and what each value does on change.
 */
export const SortButton = ({
  selectedLabel,
  qualifier,
  value,
  options,
  onChange,
  tooltip = 'Sort by',
  headerLabel = 'Sort by',
  toggle,
  compact = false,
  variant = 'pill',
  onRequestOpen,
}: SortButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSelect = (next: string) => {
    onChange(next);
    setAnchorEl(null);
  };

  // Compact hides the face, so the tooltip has to state the current sort — including the
  // qualifier, which is exactly the pairing the face exists to keep together ("Most media" over a
  // pinned-first order is the mismatch that reads as broken sorting).
  const face = qualifier ? `${selectedLabel} · ${qualifier}` : selectedLabel;

  return (
    <div>
      <Tooltip title={compact ? `${tooltip}: ${face}` : tooltip} placement="top" arrow>
        <Button
          onClick={(e) => (onRequestOpen ? onRequestOpen() : setAnchorEl(e.currentTarget))}
          className={classNames(css.sortButton, {
            [css.compact]: compact,
            [css.text]: variant === 'text',
            [css.ghost]: variant === 'ghost',
          })}
          aria-haspopup="menu"
          aria-expanded={open}
          disableRipple
          // See `compact`: with the face gone the button has no text to be named by.
          aria-label={compact ? `${tooltip}: ${face}` : undefined}
          // The text face names itself in words, so the glyph would only repeat them.
          startIcon={variant === 'text' ? null : <SwapVertRoundedIcon className={css.sortIcon} />}
          endIcon={
            compact ? null : open ? (
              <KeyboardArrowUpRoundedIcon className={css.chevron} />
            ) : (
              <KeyboardArrowDownRoundedIcon className={css.chevron} />
            )
          }
        >
          {!compact && variant === 'text' && <span className={css.textPrefix}>{headerLabel}</span>}
          {!compact && selectedLabel}
          {!compact && qualifier && <span className={css.qualifier}>· {qualifier}</span>}
        </Button>
      </Tooltip>
      <Menu
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { style: { marginTop: 8 } } }}
      >
        <MenuItem className={css.header} disableRipple>
          {headerLabel}
        </MenuItem>
        <Divider />
        {options.map((group, gi) => [
          gi > 0 && <Divider key={`divider-${gi}`} />,
          ...group.map((opt) => (
            <MenuItem
              key={opt.value}
              selected={opt.value === value}
              onClick={() => handleSelect(opt.value)}
              className={css.menuRow}
            >
              <span>{opt.label}</span>
              {opt.value === value && <CheckIcon className={css.checkIcon} fontSize="small" />}
            </MenuItem>
          )),
        ])}
        {toggle && [
          <Divider key="toggle-divider" />,
          // Stays open on click: unlike picking a sort, this is a modifier the user may want to
          // flip and compare against the option they can still see above it.
          <MenuItem
            key="toggle"
            onClick={() => toggle.onChange(!toggle.checked)}
            role="menuitemcheckbox"
            aria-checked={toggle.checked}
            className={css.menuRow}
          >
            <span>{toggle.label}</span>
            {toggle.checked && <CheckIcon className={css.checkIcon} fontSize="small" />}
          </MenuItem>,
        ]}
      </Menu>
    </div>
  );
};
